import React, { useState } from 'react';
import { Button as RoccoButton } from '@rocco/components/button';
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@rocco/components/sheet';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { FiltersButton } from '@rocco/ui/filter-sort/components/filters-modal/FiltersButton';
import { FiltersModalSection } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalSection';
import { formatToThousandsDelimiter } from '@rocco/utils/format-number';
import { pluralize } from '@rocco/utils/string-helpers';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';
import { useLatest, useUpdateEffect } from 'ahooks';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { FiltersModalElement } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { useAtom } from 'jotai';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FiltersModalSort } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalSort';

interface FiltersModalProps {
    showSorting?: boolean;
}

export const FiltersModal = (props: FiltersModalProps) => {
    const { showSorting = false } = props;
    const { searchFilters, displayMode } = useFiltersContext();
    const {
        config,
        filterStates,
        updateFilter,
        updateSort,
        getUpdatedFilterStates,
        handleAggregate,
        handleAggregateQueueClear,
    } = searchFilters;

    const isMobile = displayMode === DisplayMode.Mobile;

    const [localFilterStates, setLocalFilterStates] = useState(
        structuredClone(filterStates),
    );

    // Update filter states when initial states change, skipping the first render
    useUpdateEffect(() => {
        setLocalFilterStates(filterStates);
    }, [filterStates]);

    const [open, setOpen] = useAtom(uiStateAtomFiltersModalIsOpened);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Updates the local filter states only when the filter panel is open.
     * @param states - The new filter states to be applied
     */
    const updateLocalStates = (states: FilterStates) => {
        if (open) {
            setLocalFilterStates(states);
        }
    };

    /**
     * A hook that maintains a reference to the latest updateLocalStates function
     * to prevent unnecessary re-renders and ensure consistent access to the current value.
     * Used in handleUpdateStates to update filter states with aggregated data after successful API response.
     * The latest ref is needed because the callback in handleAggregate's onSuccess might close over a stale
     * updateLocalStates value from an old render if we didn't use useLatest
     */
    const latestUpdateLocalStates = useLatest(updateLocalStates);

    /**
     * Executes filter aggregation with the provided filter states
     * @param filterStates - The filter states to aggregate
     *
     * The function:
     * 1. Triggers filter aggregation with the provided states
     * 2. Shows loading indicator during aggregation
     * 3. Updates filter states with new total count on successful aggregation
     */
    const executeAggregate = (filterStates: FilterStates) => {
        handleAggregate({
            aggregateType: 'filterAggregation',
            filterStates: filterStates,
            onStart: () => setIsLoading(true),
            onFinish: () => setIsLoading(false),
            onSuccess: data => {
                latestUpdateLocalStates.current({
                    ...filterStates,
                    totalCount: data.totalCount,
                });
            },
        });
    };

    /**
     * Updates filter states and optionally triggers data aggregation
     * @param filterKey - Key identifying the filter to update in the filter states object
     * @param newState - New state object containing updated values for the specified filter
     * @param shouldAggregate - Flag to control whether data aggregation should be triggered after state update (defaults to true)
     *
     * The function:
     * 1. Updates the local filter states with the new state for the specified filter key
     * 2. If shouldAggregate is true:
     *    - Triggers filter aggregation with updated states
     *    - Shows loading indicator during aggregation
     *    - Updates filter states with new total count on successful aggregation
     */
    const handleUpdateStates = (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate: boolean = isMobile,
    ) => {
        const updatedFilterStates = getUpdatedFilterStates(
            localFilterStates,
            filterKey,
            newState,
        );

        setLocalFilterStates(updatedFilterStates);

        if (shouldAggregate) {
            executeAggregate(updatedFilterStates);
        }

        if (!isMobile) {
            updateFilter(updatedFilterStates);
        }
    };

    const handleAlphabeticalGrouping = (args: AggregationArgs) => {
        handleAggregate({
            ...args,
            filterStates: localFilterStates,
        });
    };

    const handleUpdateFilter = () => {
        updateFilter(localFilterStates);
        setOpen(false);
    };

    const handleClearFilters = () => {
        // Create empty filter states
        const emptyFilterStates: FilterStates = {
            ...filterStates,
            states: Object.fromEntries(
                Object.entries(filterStates.states).map(([key, value]) => {
                    if (value) {
                        if (isMultipleValuesFilterState(value)) {
                            return [
                                key,
                                {
                                    ...value,
                                    items: value.items.map(item => ({
                                        ...item,
                                        checked: false,
                                    })),
                                    selectedItems: [],
                                },
                            ];
                        }

                        if (isRangeFilterState(value)) {
                            return [
                                key,
                                {
                                    ...value,
                                    value: { ...value.range },
                                },
                            ];
                        }

                        if (isSearchableListFilterState(value)) {
                            return [
                                key,
                                {
                                    ...value,
                                    items: value.items.map(item => ({
                                        ...item,
                                        checked: false,
                                    })),
                                    selectedItems: [],
                                },
                            ];
                        }

                        if (isSingleValueFilterState(value)) {
                            return [
                                key,
                                {
                                    ...value,
                                    checked: false,
                                },
                            ];
                        }

                        if (isLocationFilterState(value)) {
                            return [
                                key,
                                {
                                    ...value,
                                    selectedLocation: null,
                                },
                            ];
                        }
                    }

                    // Default case - return original value if type not recognized
                    return [key, value];
                }),
            ) as FilterStates['states'],
        };

        handleAggregateQueueClear();
        setLocalFilterStates(emptyFilterStates);
        executeAggregate(emptyFilterStates);
    };

    const getActiveFiltersCount = () => {
        return Object.values(localFilterStates.states).reduce(
            (count, state) => {
                if (!state) return count;

                return count + (isFilterStateActive(state) ? 1 : 0);
            },
            0,
        );
    };

    const totalActiveFilters = getActiveFiltersCount();
    const totalCount = localFilterStates.totalCount ?? 0;

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <FiltersButton
                    activeFiltersCount={totalActiveFilters}
                    displayMode={displayMode}
                />
            </SheetTrigger>
            {/* Will close the modal when clicking outside the modal content */}
            <SheetContent
                color={isMobile ? 'default' : 'opaque'}
                side={isMobile ? 'bottom' : 'left'}
                onPointerDownOutside={() => setOpen(false)}
                closeContent={() => setOpen(false)}
            >
                <SheetHeader>
                    <SheetTitle>All Filters</SheetTitle>
                </SheetHeader>
                <div className="scrollbar-hidden h-[70dvh] md:h-[85dvh] w-dvw overflow-y-scroll md:px-6 md:w-[370px]">
                    {showSorting && (
                        <FiltersModalSort
                            sort={filterStates.sortBy}
                            onSortChange={sort => updateSort(sort, !isMobile)}
                        />
                    )}

                    {config.modalFilters.map(filterSection => (
                        <FiltersModalSection
                            key={`filter-${filterSection.title}`}
                            sectionConfig={filterSection}
                            filterStates={localFilterStates}
                            displayMode={displayMode}
                        >
                            {filterSection.filters.map(filterConfigItem => (
                                <FiltersModalElement
                                    key={filterConfigItem.filterKey}
                                    displayMode={displayMode}
                                    filterConfigItem={filterConfigItem}
                                    filterState={
                                        localFilterStates.states[
                                            filterConfigItem.filterKey
                                        ] ?? null
                                    }
                                    onUpdateFilterState={handleUpdateStates}
                                    onAlphabeticalGrouping={
                                        handleAlphabeticalGrouping
                                    }
                                />
                            ))}
                        </FiltersModalSection>
                    ))}
                </div>

                {isMobile && (
                    <SheetFooter>
                        <div className="flex w-full items-center justify-between">
                            <RoccoButton
                                color="transparent"
                                size="compact"
                                onClick={handleClearFilters}
                            >
                                Clear All
                            </RoccoButton>
                            <RoccoButton
                                color="secondary"
                                inverted
                                onClick={handleUpdateFilter}
                                isLoading={isLoading}
                                disabled={totalCount === 0}
                            >
                                {totalCount > 0
                                    ? `Show ${formatToThousandsDelimiter(totalCount)} ${pluralize(totalCount, config.resultName, true)}`
                                    : 'No exact matches'}
                            </RoccoButton>
                        </div>
                    </SheetFooter>
                )}
            </SheetContent>
        </Sheet>
    );
};

export const isFilterStateActive = (filterState: FilterState): boolean => {
    if (isSingleValueFilterState(filterState)) {
        return filterState.checked;
    }

    if (isMultipleValuesFilterState(filterState)) {
        return filterState.selectedItems.length > 0;
    }

    if (isRangeFilterState(filterState) && filterState.value) {
        const hasMin =
            filterState.value.min !== undefined &&
            filterState.value.min !== 0 &&
            filterState.value.min !== filterState.range.min;
        const hasMax =
            filterState.value.max !== undefined &&
            filterState.value.max !== 0 &&
            filterState.value.max !== filterState.range.max;

        return hasMin || hasMax;
    }

    if (isLocationFilterState(filterState)) {
        return !!filterState.selectedLocation?.country;
    }

    return false;
};
